import { supabase } from '../lib/supabase';

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  meta_description: string;
  keywords: string;
  tags: string;
  category: string;
  status: string;
  featured_image: string;
  reading_time: number;
  word_count: number;
  views_count: number;
  likes_count: number;
  shares_count: number;
  published_at: string;
  created_at: string;
  updated_at: string;
  author_id: number;
  schema_markup?: string;
}

export const blogService = {
  // Buscar todos os posts publicados
  async getAll(): Promise<BlogPost[]> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });
    
    if (error) {
      console.error('Erro ao buscar posts:', error);
      return [];
    }
    
    return data || [];
  },

  // Buscar posts com paginação
  async getPaginated(page: number = 1, limit: number = 12): Promise<{ posts: BlogPost[], total: number }> {
    const offset = (page - 1) * limit;
    
    const { data, error, count } = await supabase
      .from('blog_posts')
      .select('*', { count: 'exact' })
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (error) {
      console.error('Erro ao buscar posts paginados:', error);
      return { posts: [], total: 0 };
    }
    
    return { posts: data || [], total: count || 0 };
  },

  // Buscar post por slug
  async getBySlug(slug: string): Promise<BlogPost | null> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'published')
      .single();
    
    if (error) {
      console.error('Erro ao buscar post por slug:', error);
      return null;
    }
    
    return data;
  },

  // Buscar posts por categoria
  async getByCategory(category: string): Promise<BlogPost[]> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('category', category)
      .eq('status', 'published')
      .order('published_at', { ascending: false });
    
    if (error) {
      console.error('Erro ao buscar posts por categoria:', error);
      return [];
    }
    
    return data || [];
  },

  // Buscar posts relacionados (mesma categoria, excluindo o atual)
  async getRelated(currentSlug: string, category: string, limit: number = 3): Promise<BlogPost[]> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('category', category)
      .eq('status', 'published')
      .neq('slug', currentSlug)
      .order('published_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Erro ao buscar posts relacionados:', error);
      return [];
    }
    
    return data || [];
  },

  // Incrementar views
  async incrementViews(id: number): Promise<void> {
    const { error } = await supabase
      .from('blog_posts')
      .update({ views_count: supabase.sql`views_count + 1` })
      .eq('id', id);
    
    if (error) {
      console.error('Erro ao incrementar views:', error);
    }
  },

  // Buscar posts mais populares
  async getMostPopular(limit: number = 5): Promise<BlogPost[]> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'published')
      .order('views_count', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Erro ao buscar posts populares:', error);
      return [];
    }
    
    return data || [];
  },

  // Buscar posts recentes para sidebar
  async getRecent(limit: number = 5): Promise<BlogPost[]> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('id, title, slug, featured_image, published_at')
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Erro ao buscar posts recentes:', error);
      return [];
    }
    
    return data || [];
  }
};
